import React from 'react';
import { ValidationError } from './types';
import { ParsedArtistRow } from './csvParser';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import StatCard from '../ui/StatCard';
import DataTable, {
  dataTableRowClass,
  dataTableTdClass,
  dataTableThClass,
  dataTableTheadRowClass,
} from '../ui/DataTable';
import { badgeBaseClass, badgeTones } from '../ui/StatusBadge';
import Button from '../ui/Button';
import { cn } from '../../DashboardPage/lib/utils';

interface CSVPreviewProps {
  /** Every parsed row, including ones that carry errors — the preview shows all of them. */
  data: ParsedArtistRow[];
  errors: ValidationError[];
  onImport: () => void;
  onBack: () => void;
  uploading: boolean;
}

type RowStatus = 'valid' | 'warning' | 'error';

const STATUS_TONE: Record<RowStatus, string> = {
  valid: badgeTones.success,
  warning: badgeTones.warning,
  error: badgeTones.danger,
};

const STATUS_LABEL: Record<RowStatus, string> = {
  valid: 'Valid',
  warning: 'Warning',
  error: 'Error',
};

/**
 * Only rows that need attention are tinted. Every valid row used to carry a
 * green fill, which made a clean file look like a wall of colour and left the
 * rows that actually need fixing no louder than the rest.
 */
const STATUS_ROW_TINT: Record<RowStatus, string> = {
  valid: '',
  warning: 'bg-warning-50/60',
  error: 'bg-danger-50',
};

const CSVPreview: React.FC<CSVPreviewProps> = ({ data, errors, onImport, onBack, uploading }) => {
  // Add defensive programming to handle undefined errors AND data
  const safeErrors = errors || [];
  const safeData = data || [];

  /*
   * Errors are keyed by the row's line in the CSV, and `data` is compacted —
   * rows that failed parsing never make it in. Matching on array position
   * therefore attached issues to the wrong rows as soon as anything was
   * dropped, so every lookup here goes through `row.rowNumber`.
   */
  const errorsForRow = (rowNumber: number) =>
    safeErrors.filter((error) => error.row === rowNumber);

  const validRows = safeData.filter(
    (row) => !errorsForRow(row.rowNumber).some((error) => error.severity === 'error')
  );

  // Split by severity: the summary block below used to list every issue under a
  // heading that read "Must Fix Before Import", so warnings were presented as
  // blockers when they don't block anything.
  const blockingErrors = safeErrors.filter((error) => error.severity === 'error');
  const warningIssues = safeErrors.filter((error) => error.severity === 'warning');

  /*
   * Counted from the issues, not from `data`. A row rejected before it reached
   * the preview still has an error to report, and counting rows meant those
   * showed as 0 — the reported case being a file where every row was a
   * duplicate: three errors listed, "Errors 0" on the card.
   */
  const countDistinctRows = (list: typeof safeErrors) =>
    new Set(list.map((issue) => issue.row)).size;

  const errorRowCount = countDistinctRows(blockingErrors);
  const warningRowCount = countDistinctRows(
    warningIssues.filter(
      (warning) => !blockingErrors.some((error) => error.row === warning.row)
    )
  );

  const getRowStatus = (rowNumber: number): RowStatus => {
    const rowErrors = errorsForRow(rowNumber);
    if (rowErrors.some(error => error.severity === 'error')) return 'error';
    if (rowErrors.some(error => error.severity === 'warning')) return 'warning';
    return 'valid';
  };

  const columnHeaders = ['Status', 'Artist Name', 'Spotify URI', 'Apple ID', 'ASIN', 'Issues'];

  return (
    <div className="space-y-6">
      {/*
        Summary. One of the few places a per-card icon colour carries real
        meaning — valid / warning / error are mutually exclusive outcomes — so
        the badge tone is overridden rather than left uniform. The card itself
        stays white; only the badge is tinted.
      */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Valid Artists"
          value={validRows.length}
          icon={<CheckCircleIcon className="w-5 h-5" />}
          iconClassName="bg-success-50 text-success-700"
        />
        <StatCard
          label="Warnings"
          value={warningRowCount}
          icon={<ExclamationTriangleIcon className="w-5 h-5" />}
          iconClassName="bg-warning-50 text-warning-700"
        />
        <StatCard
          label="Errors"
          value={errorRowCount}
          icon={<XCircleIcon className="w-5 h-5" />}
          iconClassName="bg-danger-50 text-danger-600"
        />
      </div>

      {blockingErrors.length > 0 && (
        <div className="p-4 border rounded-lg border-danger-200 bg-danger-50">
          <h4 className="flex items-center gap-2 mb-2 text-sm font-semibold text-danger-800">
            <XCircleIcon className="w-4 h-4 shrink-0" />
            Errors — must be fixed before import
          </h4>
          <div className="space-y-1 overflow-y-auto max-h-40 scrollbar-thin">
            {blockingErrors.map((error, index) => (
              <div key={index} className="text-sm text-danger-700">
                <span className="font-medium">Row {error.row}:</span> {error.message}
                <span className="text-danger-600/70"> ({error.field})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {warningIssues.length > 0 && (
        <div className="p-4 border rounded-lg border-warning-200 bg-warning-50">
          <h4 className="flex items-center gap-2 mb-2 text-sm font-semibold text-warning-800">
            <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
            Warnings — these rows will still import
          </h4>
          <div className="space-y-1 overflow-y-auto max-h-40 scrollbar-thin">
            {warningIssues.map((warning, index) => (
              <div key={index} className="text-sm text-warning-800">
                <span className="font-medium">Row {warning.row}:</span> {warning.message}
                <span className="text-warning-700/70"> ({warning.field})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {validRows.length === 0 && safeErrors.length > 0 && (
        <div className="p-4 text-sm border rounded-lg text-danger-700 bg-danger-50 border-danger-200">
          All rows have errors. Fix the issues above and re-upload your CSV.
        </div>
      )}

      {/* Preview Table — sticky header so the columns stay readable while scrolling. */}
      <DataTable
        className="max-h-96 overflow-y-auto"
        columns={columnHeaders.map((header) => ({ key: header, header }))}
        head={
          <thead>
            <tr className={dataTableTheadRowClass}>
              {columnHeaders.map((header) => (
                <th
                  key={header}
                  scope="col"
                  className={cn(dataTableThClass, 'sticky top-0 z-10 bg-primary-50')}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        }
      >
        {safeData.map((row) => {
          const status = getRowStatus(row.rowNumber);
          const rowErrors = errorsForRow(row.rowNumber);
          return (
            <tr key={row.rowNumber} className={cn(dataTableRowClass, STATUS_ROW_TINT[status])}>
              <td className={dataTableTdClass}>
                <span className={cn(badgeBaseClass, STATUS_TONE[status])}>
                  {STATUS_LABEL[status]}
                </span>
              </td>
              <td className={cn(dataTableTdClass, 'font-medium')}>{row['artistName']}</td>
              <td className={cn(dataTableTdClass, 'font-mono text-neutral-600')}>
                {row['spotifyURI'] || <span className="font-sans text-neutral-400">—</span>}
              </td>
              <td className={cn(dataTableTdClass, 'font-mono text-neutral-600')}>
                {row['appleID'] || <span className="font-sans text-neutral-400">—</span>}
              </td>
              <td className={cn(dataTableTdClass, 'font-mono text-neutral-600')}>
                {row['asin'] || <span className="font-sans text-neutral-400">—</span>}
              </td>
              <td className={cn(dataTableTdClass, 'whitespace-normal')}>
                {rowErrors.length > 0 ? (
                  <div className="space-y-1">
                    {rowErrors.map((error, i) => (
                      <div
                        key={i}
                        className={cn(
                          'text-xs',
                          error.severity === 'error' ? 'text-danger-700' : 'text-warning-800'
                        )}
                      >
                        {error.field}: {error.message}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-neutral-400">—</span>
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>

      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="neutral" onClick={onBack} disabled={uploading}>
          Back to Upload
        </Button>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          {errorRowCount > 0 && (
            <p className="text-sm text-danger-700">
              {errorRowCount} {errorRowCount === 1 ? 'row' : 'rows'} will be skipped.
            </p>
          )}

          {validRows.length > 0 && (
            /*
              The parent's onImport owns the outcome dialog. This used to fire its
              own "Import Complete!" alert here, synchronously and before the
              request had even started — so it reported success even when the
              import failed, on top of the parent's own message.
            */
            <Button onClick={onImport} loading={uploading}>
              {uploading ? 'Importing...' : `Import ${validRows.length} Valid Artists`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVPreview;
